import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { FormToggle } from "@/components/form/FormToggle";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

import type { DocumentModel } from "@/models/DocumentModel";

export const PublicAccessToggle = observer(({ doc }: { doc: DocumentModel }) => {
  const t = useTranslations();
  const { publicRead, publicWrite } = doc;
  const isChecked = publicRead;

  const title = t("share.publicAccess");
  const description = publicWrite
    ? t("share.publicAccessEditDescription")
    : t("share.publicAccessViewDescription");

  const lockedTitle = t("share.onlyInvitedPeople");
  const unlockedTitle = t("share.anyoneWithLink");

  const handleOnToggleChange = async () => {
    if (publicRead) {
      await doc.api.updatePublicFlags({ publicRead: false, publicWrite: false });
      if (doc.id) {
        trackEvent("share_permission_group_changed", {
          setting: lockedTitle,
          doc_id: doc.id,
        });
      }
    } else {
      await doc.api.updatePublicFlags({ publicRead: true, publicWrite: false });
      if (doc.id) {
        trackEvent("share_permission_group_changed", {
          setting: unlockedTitle,
          doc_id: doc.id,
        });
      }
    }
  };

  return (
    <button
      id="public-access-toggle"
      type="button"
      onClick={handleOnToggleChange}
      className={twMerge(
        "flex items-center max-w-[260px] hover:bg-blue-100 hover:dark:bg-blue-900 p-1.5 rounded-8",
        "sm:max-w-[200px]",
      )}
    >
      <FormToggle checked={isChecked} />
      <span className="flex flex-col ml-3 text-left tracking-tight font-geist leading-[1.3]">
        <span className="text-[16px]">{title}</span>
        <span className="whitespace-pre-wrap text-[14px] text-gray-500">{description}</span>
      </span>
    </button>
  );
});
